/**
 * Tiny hash router. Maps `#/` → home, `#/recognize`, `#/count`, `#/add`, `#/sub`.
 * Hash routing avoids ingress/nginx route configuration; a deep link works
 * without any server-side rewrite.
 */

export type Route = 'home' | 'recognize' | 'count' | 'add' | 'sub';

const HASH_TO_ROUTE: Record<string, Route> = {
  '': 'home',
  '#/': 'home',
  '#/recognize': 'recognize',
  '#/count': 'count',
  '#/add': 'add',
  '#/sub': 'sub',
};

const ROUTE_TO_HASH: Record<Route, string> = {
  home: '#/',
  recognize: '#/recognize',
  count: '#/count',
  add: '#/add',
  sub: '#/sub',
};

export function parseRoute(hash: string): Route {
  return HASH_TO_ROUTE[hash] ?? 'home';
}

export function navigate(route: Route): void {
  const target = ROUTE_TO_HASH[route];
  if (window.location.hash !== target) {
    window.location.hash = target;
  }
}

type RouteListener = (route: Route) => void;

export class HashRouter {
  private listeners = new Set<RouteListener>();
  private _current: Route;
  private boundHandler = (): void => this.onHashChange();

  constructor() {
    this._current = parseRoute(window.location.hash);
  }

  start(): void {
    window.addEventListener('hashchange', this.boundHandler);
  }

  stop(): void {
    window.removeEventListener('hashchange', this.boundHandler);
  }

  get current(): Route {
    return this._current;
  }

  subscribe(listener: RouteListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private onHashChange(): void {
    const next = parseRoute(window.location.hash);
    if (next === this._current) return;
    this._current = next;
    for (const listener of this.listeners) listener(next);
  }
}
